import { DASHBOARD_LOCALE } from './messages';

// Intl formatters are expensive to build; table cells and charts format thousands of values.
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

/** A cached `Intl.NumberFormat` for the dashboard locale. */
export function numberFormat(options: Intl.NumberFormatOptions = {}) {
  const key = JSON.stringify(options);
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(DASHBOARD_LOCALE, options);
    numberFormats.set(key, format);
  }
  return format;
}

/** A cached `Intl.DateTimeFormat` for the dashboard locale. */
export function dateTimeFormat(options: Intl.DateTimeFormatOptions = {}) {
  const key = JSON.stringify(options);
  let format = dateTimeFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(DASHBOARD_LOCALE, options);
    dateTimeFormats.set(key, format);
  }
  return format;
}

type Nullable<T> = T | null | undefined;

interface FallbackOption {
  /** Shown for a missing (or unparseable) value. Defaults to `-`. */
  fallback?: string | undefined;
}

export function formatNumber(value: Nullable<number>, digits = 0) {
  if (value === null || value === undefined) return '-';
  return numberFormat({ maximumFractionDigits: digits }).format(value);
}

export function formatCurrency(value: Nullable<number>) {
  return formatUsd(value);
}

/** US dollars, two decimals unless `maximumFractionDigits` asks for more (or fewer). */
export function formatUsd(
  value: Nullable<number>,
  {
    fallback = '-',
    minimumFractionDigits,
    maximumFractionDigits = 2,
  }: FallbackOption & { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
) {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return numberFormat({
    style: 'currency',
    currency: 'USD',
    ...(minimumFractionDigits === undefined
      ? { minimumFractionDigits: Math.min(2, maximumFractionDigits) }
      : { minimumFractionDigits }),
    maximumFractionDigits,
  }).format(value);
}

/** A value already expressed in percent, e.g. `12.5` → `12.5%`. */
export function formatPercent(
  value: Nullable<number>,
  {
    fallback = '-',
    maximumFractionDigits = 2,
  }: FallbackOption & { maximumFractionDigits?: number } = {},
) {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return `${numberFormat({ maximumFractionDigits }).format(value)}%`;
}

/** A fraction shown as percent, e.g. `0.125` → `12.5%`. */
export function formatRatio(value: Nullable<number>) {
  return formatPercent(percentValue(value));
}

export function percentValue(value: Nullable<number>) {
  return value === null || value === undefined ? value : value * 100;
}

/**
 * A date (or date and time, given `hour`/`minute` options) in the dashboard locale; `M/D/YYYY` by
 * default. Missing values show `fallback`; unparseable ones too, unless `keepInvalid` shows them
 * as received.
 */
export function formatDisplayDate(
  value: Nullable<string | number | Date>,
  {
    fallback = '-',
    keepInvalid = false,
    ...options
  }: FallbackOption & { keepInvalid?: boolean } & Intl.DateTimeFormatOptions = {},
) {
  if (value === null || value === undefined || value === '') return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isNaN(date.getTime())) return dateTimeFormat(options).format(date);
  return keepInvalid ? String(value) : fallback;
}
